import React from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneOutgoing, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Conversation } from '@/types/Conversation';
import {
  BOOKING_CHANGE_LABEL,
  BOOKING_CHANGE_STYLE,
} from '@/utils/bookingChange';

/**
 * What came out of a call, at a glance.
 *
 * One component for both layouts so the desktop column and the mobile card
 * can never drift apart on which flags exist or what they look like, and the
 * booking icons and tones come from the same place as the notification
 * bell's, so the list and the bell agree about the same call.
 *
 * Deliberately not the billing badge: that says how a call is counted, which
 * is a different question from what happened on it, and it already has its
 * own place next to the duration.
 */
interface ConversationFlagsProps {
  conversation: Conversation;
  className?: string;
  /** Icon only, for the narrow mobile row where the label will not fit. */
  compact?: boolean;
}

interface Flag {
  key: string;
  icon: LucideIcon;
  label: string;
  tone: string;
}

const BADGE_CLASS =
  'inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide';

export const ConversationFlags: React.FC<ConversationFlagsProps> = ({
  conversation,
  className,
  compact = false,
}) => {
  const { t } = useTranslation();
  const flags: Flag[] = [];

  if (conversation.callback_requested) {
    flags.push({
      key: 'callback',
      icon: PhoneOutgoing,
      label: t(
        'agent.conversations.filters.needs_callback.translation',
        'Needs callback'
      ),
      // Brand colour: this is the one that asks the owner to do something.
      tone: 'bg-brand/10 text-brand',
    });
  }

  const change = conversation.booking_change;
  if (change) {
    const label = BOOKING_CHANGE_LABEL[change];
    flags.push({
      key: 'booking',
      icon: BOOKING_CHANGE_STYLE[change].icon,
      label: t(label.key, label.fallback),
      tone: BOOKING_CHANGE_STYLE[change].tone,
    });
  }

  if (flags.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {flags.map(({ key, icon: Icon, label, tone }) => (
        <span
          key={key}
          className={cn(BADGE_CLASS, tone)}
          title={compact ? label : undefined}
        >
          <Icon className="h-3 w-3" aria-hidden />
          {compact ? <span className="sr-only">{label}</span> : label}
        </span>
      ))}
    </div>
  );
};

export default ConversationFlags;
